import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const TradeFacilitation = () => {
  const [calculatorType, setCalculatorType] = useState('shipping');
  const [origin, setOrigin] = useState('');
  const [destination, setDestination] = useState('');
  const [weight, setWeight] = useState('');
  const [value, setValue] = useState('');

  const services = [
    {
      id: 1,
      title: 'Documentation Support',
      description: 'Complete assistance with trade documentation, customs clearance, and regulatory compliance.',
      icon: 'FileText',
      color: 'bg-primary',
      features: [
        'Invoice preparation',
        'Certificate of origin',
        'Customs documentation',
        'Compliance verification'
      ]
    },
    {
      id: 2,
      title: 'Logistics Coordination',
      description: 'End-to-end logistics management from pickup to delivery with real-time tracking.',
      icon: 'Truck',
      color: 'bg-secondary',
      features: [
        'Multi-modal transport',
        'Warehouse management',
        'Real-time tracking',
        'Last-mile delivery'
      ]
    },
    {
      id: 3,
      title: 'Quality Inspection',
      description: 'Pre-shipment quality checks and third-party inspection services to ensure product standards.',
      icon: 'Shield',
      color: 'bg-accent',
      features: [
        'Factory audits',
        'Product inspection',
        'Quality certification',
        'Compliance testing'
      ]
    },
    {
      id: 4,
      title: 'Trade Finance',
      description: 'Flexible financing solutions including letters of credit, trade credit, and payment terms.',
      icon: 'CreditCard',
      color: 'bg-success',
      features: [
        'Letter of credit',
        'Trade credit insurance',
        'Payment guarantees',
        'Currency hedging'
      ]
    }
  ];

  const countries = [
    { value: 'india', label: 'India' },
    { value: 'china', label: 'China' },
    { value: 'usa', label: 'United States' },
    { value: 'germany', label: 'Germany' },
    { value: 'japan', label: 'Japan' },
    { value: 'uk', label: 'United Kingdom' }
  ];

  const documents = [
    {
      name: 'Commercial Invoice Template',
      type: 'PDF',
      size: '245 KB',
      icon: 'FileText'
    },
    {
      name: 'Packing List Format',
      type: 'XLSX',
      size: '128 KB',
      icon: 'Sheet'
    },
    {
      name: 'Certificate of Origin',
      type: 'PDF',
      size: '312 KB',
      icon: 'Award'
    },
    {
      name: 'Bill of Lading Template',
      type: 'PDF',
      size: '198 KB',
      icon: 'FileText'
    }
  ];

  const calculateEstimate = () => {
    if (!origin || !destination || !weight || !value) {
      return null;
    }

    const baseRate = 150;
    const weightCost = parseFloat(weight) * 45;
    const insuranceCost = (parseFloat(value) * 0.02);
    const total = baseRate + weightCost + insuranceCost;

    return {
      shipping: weightCost?.toFixed(2),
      insurance: insuranceCost?.toFixed(2),
      handling: baseRate?.toFixed(2),
      total: total?.toFixed(2)
    };
  };

  const estimate = calculateEstimate();

  return (
    <section className="py-12 md:py-16 lg:py-20 bg-background">
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
        <div className="text-center mb-8 md:mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full text-sm md:text-base text-primary mb-4">
            <Icon name="Package" size={20} />
            <span>Trade Facilitation Services</span>
          </div>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-foreground mb-4">
            Seamless Trade Solutions
          </h2>
          <p className="text-base md:text-lg text-muted-foreground max-w-3xl mx-auto">
            Comprehensive support for every stage of your international trade journey
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12 md:mb-16">
          {services?.map((service) => (
            <div
              key={service?.id}
              className="bg-card rounded-xl p-6 shadow-lg border border-border hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <div className={`w-12 h-12 md:w-14 md:h-14 ${service?.color} rounded-xl flex items-center justify-center mb-4`}>
                <Icon name={service?.icon} size={24} color="white" />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-foreground mb-3">
                {service?.title}
              </h3>
              <p className="text-sm text-muted-foreground mb-4">
                {service?.description}
              </p>
              <ul className="space-y-2">
                {service?.features?.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2 text-xs md:text-sm text-muted-foreground">
                    <Icon name="Check" size={14} color="var(--color-success)" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12 md:mb-16">
          <div className="bg-card rounded-2xl p-6 md:p-8 shadow-lg border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Calculator" size={24} color="var(--color-primary)" />
              </div>
              <div>
                <h3 className="text-xl md:text-2xl font-bold text-foreground">Trade Cost Calculator</h3>
                <p className="text-sm text-muted-foreground">Estimate your shipping costs</p>
              </div>
            </div>

            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Select
                  label="Origin Country"
                  options={countries}
                  value={origin}
                  onChange={setOrigin}
                  placeholder="Select origin"
                  required
                />
                <Select
                  label="Destination Country"
                  options={countries}
                  value={destination}
                  onChange={setDestination}
                  placeholder="Select destination"
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Input
                  label="Weight (kg)"
                  type="number"
                  placeholder="Enter weight"
                  value={weight}
                  onChange={(e) => setWeight(e?.target?.value)}
                  required
                />
                <Input
                  label="Cargo Value (₹)"
                  type="number"
                  placeholder="Enter value"
                  value={value}
                  onChange={(e) => setValue(e?.target?.value)}
                  required
                />
              </div>

              {estimate && (
                <div className="mt-6 p-4 bg-muted rounded-xl space-y-3">
                  <h4 className="font-semibold text-foreground mb-3">Cost Breakdown</h4>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Shipping Cost</span>
                    <span className="font-semibold text-foreground">₹{estimate?.shipping}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Insurance</span>
                    <span className="font-semibold text-foreground">₹{estimate?.insurance}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Handling Charges</span>
                    <span className="font-semibold text-foreground">₹{estimate?.handling}</span>
                  </div>
                  <div className="pt-3 border-t border-border flex justify-between">
                    <span className="font-bold text-foreground">Total Estimate</span>
                    <span className="font-bold text-primary text-lg">₹{estimate?.total}</span>
                  </div>
                </div>
              )}

              <Button
                variant="default"
                fullWidth
                iconName="Send"
                iconPosition="right"
              >
                Get Detailed Quote
              </Button>
            </div>
          </div>

          <div className="space-y-6">
            <div className="bg-card rounded-2xl p-6 md:p-8 shadow-lg border border-border">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                  <Icon name="Download" size={24} color="var(--color-accent)" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-foreground">Document Templates</h3>
                  <p className="text-sm text-muted-foreground">Ready-to-use trade documents</p>
                </div>
              </div>

              <div className="space-y-3">
                {documents?.map((doc, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-muted rounded-lg hover:bg-muted/80 transition-colors cursor-pointer"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name={doc?.icon} size={20} color="var(--color-primary)" />
                      </div>
                      <div>
                        <div className="text-sm font-semibold text-foreground">{doc?.name}</div>
                        <div className="text-xs text-muted-foreground">{doc?.type} • {doc?.size}</div>
                      </div>
                    </div>
                    <Icon name="Download" size={20} color="var(--color-primary)" />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gradient-to-br from-success to-success/80 rounded-2xl p-6 md:p-8 text-white shadow-lg">
              <Icon name="Headphones" size={40} className="mb-4" />
              <h3 className="text-xl md:text-2xl font-bold mb-3">24/7 Trade Support</h3>
              <p className="text-sm md:text-base text-white/90 mb-6">
                Our expert team is available round the clock to assist with your trade queries and documentation needs.
              </p>
              <Button
                variant="default"
                size="lg"
                iconName="Phone"
                iconPosition="left"
                className="bg-white text-success hover:bg-white/90"
              >
                Contact Support
              </Button>
            </div>
          </div>
        </div>

        <div className="bg-card rounded-2xl p-6 md:p-8 lg:p-12 shadow-lg border border-border">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h3 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
                Compliance & Regulatory Support
              </h3>
              <p className="text-base md:text-lg text-muted-foreground mb-6">
                Navigate complex international trade regulations with confidence. Our compliance experts ensure your shipments meet all legal requirements.
              </p>
              <ul className="space-y-3 mb-6">
                {[
                  'Customs clearance assistance',
                  'Import/export license support',
                  'Tariff classification guidance',
                  'Regulatory compliance checks',
                  'Trade agreement utilization'
                ]?.map((item, index) => (
                  <li key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 bg-success/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon name="Check" size={14} color="var(--color-success)" />
                    </div>
                    <span className="text-sm md:text-base text-foreground">{item}</span>
                  </li>
                ))}
              </ul>
              <Button
                variant="default"
                size="lg"
                iconName="Shield"
                iconPosition="left"
              >
                Learn More About Compliance
              </Button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="bg-primary/10 rounded-xl p-4 md:p-6 text-center">
                <Icon name="FileCheck" size={32} color="var(--color-primary)" className="mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">100%</div>
                <div className="text-xs md:text-sm text-muted-foreground">Compliance Rate</div>
              </div>
              <div className="bg-success/10 rounded-xl p-4 md:p-6 text-center">
                <Icon name="Clock" size={32} color="var(--color-success)" className="mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">48hrs</div>
                <div className="text-xs md:text-sm text-muted-foreground">Avg. Clearance</div>
              </div>
              <div className="bg-accent/10 rounded-xl p-4 md:p-6 text-center">
                <Icon name="Globe" size={32} color="var(--color-accent)" className="mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">45+</div>
                <div className="text-xs md:text-sm text-muted-foreground">Countries</div>
              </div>
              <div className="bg-secondary/10 rounded-xl p-4 md:p-6 text-center">
                <Icon name="Award" size={32} color="var(--color-secondary)" className="mx-auto mb-3" />
                <div className="text-2xl md:text-3xl font-bold text-foreground mb-1">15+</div>
                <div className="text-xs md:text-sm text-muted-foreground">Certifications</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default TradeFacilitation;